import React, { useState } from 'react';
import { FEATURES, PLAYLIST_INFO_ACTIONS } from '@soundxyz/utils/constants/logging';
import { CoverImages } from '@components/Playlists/basics/CoverImages';
import { MintsDrivenTrigger } from '@components/Playlists/basics/MintsDrivenTrigger';
import { ChartCard } from '@components/basics/ChartCard';
import { ChartCardContext } from '@components/basics/ChartCard/Context';
import { AddToQueueMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToQueueMenuItem';
import { CopyLinkMenuItem } from '@components/basics/Dropdowns/MenuItems/CopyLinkMenuItem';
import { ShareMenuItem } from '@components/basics/Dropdowns/MenuItems/ShareMenuItem';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, gql, PlaylistChartCardFragmentDoc } from '@graphql/reactQuery';
import { useLogInfo } from '@hooks/useLogInfo';
import { usePlaylistPlayingState } from '@hooks/usePlaylistPlayingState';
import { trackClickEvent } from '@lib/analytics';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PlaylistChartCard on PlaylistChartRank {
    id
    currentRank
    trendingIndicator
    playlist {
      id
      numReferralPurchases
      name
      trackIds
      webappUri
      coverImage {
        id
        url
      }
      user {
        id
        username
        webappUri
      }
      ...PlaylistCoverImages
      ...PlaylistShareInfo
    }
  }
`);

export const PlaylistChartCard = ({
  chart,
}: {
  chart: FragmentType<PlaylistChartCardFragmentDoc>;
}) => {
  const logInfo = useLogInfo();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { playlist, currentRank, trendingIndicator } = getFragment(
    PlaylistChartCardFragmentDoc,
    chart,
  );

  const { isPlaying, onPlayButtonClick, loading } = usePlaylistPlayingState({
    playlistId: playlist.id,
  });
  const onClick = () => {
    logInfo({
      feature: FEATURES.PLAYLISTS,
      isArtistOnlyPage: false,
      message: 'Playlist chart card play button clicked',
      action: PLAYLIST_INFO_ACTIONS.PLAYLIST_CHART_CARD_PLAY_CLICK,
      data: {
        playlistId: playlist.id,
      },
    });
    onPlayButtonClick();
  };

  return (
    <ChartCardContext.Provider value={{ isPlaying: isPlaying, minimal: false }}>
      <div className="group flex w-full items-center justify-start gap-1 border-base200 py-2.5 md:border-b md:py-4">
        <ChartCard.Cover
          isBufferingChart={loading}
          image={
            <div className="h-full w-full transition-transform duration-[0.4s] md:group-hover:scale-[1.2]">
              <CoverImages playlist={playlist} maxSize={176} priority={false} />
            </div>
          }
          rank={currentRank}
          change={trendingIndicator}
          onPlayButtonClick={onClick}
          playButtonPrefetchRef={null}
          disablePlay={playlist.trackIds.length === 0}
          currentListeningParty={null}
        />
        <div className="ml-2 md:ml-6">
          <ChartCard.Info
            header={
              <AccessibleLink pathnameUri={webappUriPath(playlist.webappUri)}>
                {playlist.name}
              </AccessibleLink>
            }
            subheader={
              <AccessibleLink pathnameUri={webappUriPath(playlist.user.webappUri)}>
                {playlist.user.username}
              </AccessibleLink>
            }
            stats={
              playlist.numReferralPurchases ? (
                <MintsDrivenTrigger
                  className="mt-1 text-base-xs text-base500 transition-[filter] duration-300 hover:brightness-[70%] md:text-base-s"
                  playlistId={playlist.id}
                  mintsDriven={playlist.numReferralPurchases}
                  infoAction={PLAYLIST_INFO_ACTIONS.PLAYLIST_CHART_CARD_MINTS_DRIVEN_CLICK}
                />
              ) : null
            }
          />
        </div>
        <div className="ml-auto">
          <ThreeDotsDropdown isDropdownOpen={isDropdownOpen} setIsDropdownOpen={setIsDropdownOpen}>
            <AddToQueueMenuItem
              trackIds={playlist.trackIds}
              disabled={!playlist.trackIds || playlist.trackIds.length === 0}
              emitAnalytics={() => {
                trackClickEvent({
                  event: 'Add to Queue Clicked',
                  properties: {
                    associationId: playlist.id,
                    associationType: 'Playlist',
                    sourceComponent: 'Chart',
                  },
                });
              }}
            />
            <CopyLinkMenuItem url={playlist.webappUri} />
            <ShareMenuItem playlist={playlist} source="Chart" />
          </ThreeDotsDropdown>
        </div>
      </div>
    </ChartCardContext.Provider>
  );
};

export const PlaylistChartCardSkeleton = ({ rows }: { rows: number }) => (
  <>
    {[...Array(rows)].map((_, i) => (
      <ChartCardContext.Provider key={i} value={{ isLoadingSummary: true, minimal: false }}>
        <div className="flex w-full items-center justify-start gap-1 border-base200 py-2.5 md:border-b md:py-4">
          <ChartCard.Cover
            image={null}
            onPlayButtonClick={() => null}
            playButtonPrefetchRef={null}
            currentListeningParty={null}
          />
          <div className="ml-2 md:ml-6">
            <ChartCard.Info header="" subheader="" stats={null} />
          </div>
        </div>
      </ChartCardContext.Provider>
    ))}
  </>
);
